import type { FC, ReactEventHandler } from 'react'

import { Radio, RadioGroup } from '@skytech/ui'
import { Image } from '@skytech/ui/Image'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon'

import Text from '@/app/translations/Text'
import type { Asset } from '@/app/types'
import { useOffBoardingForm } from '@/forms/hooks'

import './AssetHandling.styles.scss'

const AssetHandling: FC<{ currency: string }> = ({ currency }) => {
  const { data } = useOffBoardingForm()

  if (!data || data.assets.length === 0) return null

  const { assets, isEditable } = data
  const mayBuyAnyAsset = assets.some(asset => asset.isBuyoutAllowed)

  return (
    <section className="asset-handling">
      <h3>
        <Text id="Utstyr" />
      </h3>

      {mayBuyAnyAsset && (
        <p>
          <Text id="Hva ønsker du å gjøre med ditt utstyr?" />
        </p>
      )}

      {Object.values(assets).map(asset =>
        asset.isBuyoutAllowed ? (
          <Choices key={asset.id} asset={asset} isEditable={isEditable} currency={currency} />
        ) : null
      )}

      {Object.values(assets).map(asset =>
        !asset.isBuyoutAllowed && asset.isAssetReturnRequired ? (
          <Information key={asset.id} description={asset.description} img={asset.imgUrl} />
        ) : null
      )}
    </section>
  )
}

const imagePlaceholder: ReactEventHandler<HTMLImageElement> = e => {
  ;(e.target as HTMLImageElement).src = '/cellphoneIcon.png'
}

const Choices: FC<{ asset: Asset; isEditable: boolean; currency: string }> = ({
  asset,
  isEditable,
  currency,
}) => {
  const { description, priceReturn, priceBuyout, id } = asset

  return (
    <div className="asset-choices">
      <h4>{description}</h4>

      <div className="asset-radios">
        <div className="asset-picture">
          <Image src={asset.imgUrl || ''} alt="asset" onError={imagePlaceholder} />
        </div>

        <RadioGroup id="buyout-type">
          <Field name={id}>
            {({ field, form }: FieldProps) => (
              <Radio
                id={'RETURN' + id}
                label={
                  <Text
                    id="Hand in: entails {currency} {returnPrice},- for salary deduction"
                    currency={currency}
                    returnPrice={priceReturn.toLocaleString('no', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  />
                }
                marginBottom={16}
                isDisabled={!isEditable}
                {...field}
                checked={form.values[id] === 'RETURN'}
                customValue="RETURN"
              />
            )}
          </Field>

          <Field name={id}>
            {({ field, meta, form }: FieldProps) => (
              <Radio
                id={'BUYOUT' + id}
                label={
                  <Text
                    id="Buy out: entails {currency} {buyoutPrice},- for salary deduction"
                    currency={currency}
                    buyoutPrice={priceBuyout.toLocaleString('no', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  />
                }
                marginBottom={16}
                isDisabled={!isEditable}
                error={meta.error}
                touched={meta.touched}
                {...field}
                checked={form.values[id] === 'BUYOUT'}
                customValue="BUYOUT"
              />
            )}
          </Field>
        </RadioGroup>
      </div>
    </div>
  )
}

const Information: FC<{ description: string; img: string }> = ({ description, img }) => (
  <div className="asset-information">
    <div className="asset-picture">
      <Image src={img || ''} alt="asset" onError={imagePlaceholder} />
    </div>

    <AlertCircleOutlineIcon />

    <Text
      id="Husk å levere inn din {description} til IT."
      description={<strong>{description}</strong>}
    />
  </div>
)

export default AssetHandling
